/**
 * Opções e rótulos das projeções comerciais.
 *
 * Espelham os enums do backend (`Entities/Enums/SolicitacaoProjecaoComercial`) — o `id` de cada
 * opção é o valor gravado no banco. Mantenha os dois lados em sincronia ao adicionar um valor.
 *
 * Tudo que a tela exibe (coluna do kanban, card, modal) lê daqui, para não repetir rótulo
 * nem cor em cada superfície.
 */

import type { SelectOptions } from '@/models/basic/select-options.model'

/** Papel que enxerga o kanban e move os cards. Ainda será criado no Keycloak. */
export const ROLE_RESPONSAVEL = 'projecao_comercial_responsavel'

export const ROLES_KANBAN: string[] = ['admin', ROLE_RESPONSAVEL]

/** Quem pode abrir a solicitação de estimativa de numerário (formulário de projeção). */
export const ROLES_SOLICITACAO: string[] = [
  'admin',
  'colaborador_prana',
  'colaborador_satya',
]

// `as const` em cada mapa: os valores viram literais, então trocar `MODELO.TENHO_DI_DUIMP` por um
// número que não existe no enum vira erro de tipo em quem compara.
export const MODELO = {
  TENHO_DI_DUIMP: 1,
  TENHO_INVOICE: 2,
  TENHO_SOMENTE_INFORMACOES: 3
} as const

export type ModeloSolicitacao = (typeof MODELO)[keyof typeof MODELO]

export const MODELO_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: MODELO.TENHO_DI_DUIMP, nome: 'Tenho a DI / DUIMP' },
  { id: MODELO.TENHO_INVOICE, nome: 'Tenho a Invoice' },
  { id: MODELO.TENHO_SOMENTE_INFORMACOES, nome: 'Tenho somente as informações' }
]

export const TIPO = {
  COMPARATIVO: 1,
  SIMULACAO: 2
} as const

export const TIPO_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: TIPO.COMPARATIVO, nome: 'Comparativo (ex: Op. própria x Encomenda)' },
  { id: TIPO.SIMULACAO, nome: 'Simulação (Projeção de custo)' }
]

export const SETOR_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: 1, nome: 'Comercial' },
  { id: 2, nome: 'Operação' },
  { id: 3, nome: 'Satya' },
  { id: 4, nome: 'Outros' }
]

export const ENQUADRAMENTO_TRIBUTARIO_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: 1, nome: 'Lucro real' },
  { id: 2, nome: 'Lucro presumido' },
  { id: 3, nome: 'Simples nacional' }
]

export const CONTRIBUINTE_ICMS_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: 1, nome: 'Sim' },
  { id: 2, nome: 'Não' },
  { id: 3, nome: 'Não informado' }
]

export const MODAL_TRANSPORTE = {
  MARITIMO: 1,
  AEREO: 2,
  RODOVIARIO: 3
} as const

export const MODAL_TRANSPORTE_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: MODAL_TRANSPORTE.MARITIMO, nome: 'Marítimo' },
  { id: MODAL_TRANSPORTE.AEREO, nome: 'Aéreo' },
  { id: MODAL_TRANSPORTE.RODOVIARIO, nome: 'Rodoviário' }
]

export const PLACEHOLDER_OPERACAO: SelectOptions = { id: null, nome: 'Selecione...' }

export const OPERACOES_PRANA: SelectOptions[] = [
  { id: 3, nome: 'PRANA - Conta e Ordem' },
  { id: 1, nome: 'PRANA - Encomenda RO' },
  { id: 2, nome: 'PRANA - Encomenda SC' }
]

export const OPERACOES_CLIENTE: SelectOptions[] = [
  { id: 4, nome: 'CLIENTE - Conta Própria' },
  { id: 5, nome: 'CLIENTE - Trade Conta e Ordem' },
  { id: 6, nome: 'CLIENTE - Trade Encomenda RO' },
  { id: 7, nome: 'CLIENTE - Trade Encomenda SC' }
]

export const TIPO_OPERACAO_OPTIONS: SelectOptions[] = [
  PLACEHOLDER_OPERACAO,
  ...OPERACOES_PRANA,
  ...OPERACOES_CLIENTE
]

export const TIPO_OPERACAO_POR_TIPO: Record<number, SelectOptions[]> = {
  [TIPO.SIMULACAO]: [PLACEHOLDER_OPERACAO, ...OPERACOES_PRANA],
  [TIPO.COMPARATIVO]: [PLACEHOLDER_OPERACAO, ...OPERACOES_PRANA, ...OPERACOES_CLIENTE]
}

/** Quando o tipo é Comparativo, PRANA e CLIENTE viram dois campos de operação separados. */
export const TIPO_OPERACAO_PRANA_OPTIONS: SelectOptions[] = [
  PLACEHOLDER_OPERACAO,
  ...OPERACOES_PRANA
]

export const TIPO_OPERACAO_CLIENTE_OPTIONS: SelectOptions[] = [
  PLACEHOLDER_OPERACAO,
  ...OPERACOES_CLIENTE
]

export const FINALIDADE_PRODUTO_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: 1, nome: 'Revenda' },
  { id: 2, nome: 'Uso e Consumo' },
  { id: 3, nome: 'Ativo' },
  { id: 4, nome: 'Industrialização' }
]

export const SIM_NAO = { SIM: 1, NAO: 2 } as const

export const SIM_NAO_OPTIONS: SelectOptions[] = [
  { id: null, nome: 'Selecione...' },
  { id: SIM_NAO.SIM, nome: 'Sim' },
  { id: SIM_NAO.NAO, nome: 'Não' }
]

export const SITUACAO = {
  NOVA: 1,
  AGUARDANDO_INFORMACOES: 2,
  EM_ANDAMENTO_COMERCIAL: 3,
  EM_ANDAMENTO_OPERACIONAL: 4,
  CONCLUIDA: 5,
  ARQUIVADA: 6
} as const

export type SituacaoProjecao = (typeof SITUACAO)[keyof typeof SITUACAO]

/**
 * Cor de cada coluna/situação, em tokens do design system. Consumida pelo `StatusBadge`
 * (card e modal) e pela régua superior da coluna do kanban — mesma fonte nos três lugares.
 *
 * `Record<SituacaoProjecao, string>` obriga a preencher a cor de toda situação nova.
 */
export const SITUACAO_CORES: Record<SituacaoProjecao, string> = {
  [SITUACAO.NOVA]: 'var(--feedback-info)',
  [SITUACAO.AGUARDANDO_INFORMACOES]: 'var(--feedback-warning)',
  [SITUACAO.EM_ANDAMENTO_COMERCIAL]: 'var(--action-primary)',
  [SITUACAO.EM_ANDAMENTO_OPERACIONAL]: 'var(--action-accent)',
  [SITUACAO.CONCLUIDA]: 'var(--feedback-success)',
  [SITUACAO.ARQUIVADA]: 'var(--feedback-neutral)'
}

export const corDaSituacao = (situacaoId?: number | null): string =>
  SITUACAO_CORES[situacaoId as SituacaoProjecao] ?? SITUACAO_CORES[SITUACAO.ARQUIVADA]

/**
 * Rótulo do botão de cada transição. O backend decide *quais* transições estão disponíveis;
 * aqui só se define como cada uma é chamada na tela.
 */
export const ROTULO_TRANSICAO: Record<SituacaoProjecao, string> = {
  [SITUACAO.NOVA]: 'Devolver para novas',
  [SITUACAO.AGUARDANDO_INFORMACOES]: 'Aguardar informações',
  [SITUACAO.EM_ANDAMENTO_COMERCIAL]: 'Iniciar no Comercial',
  [SITUACAO.EM_ANDAMENTO_OPERACIONAL]: 'Enviar para Operacional',
  [SITUACAO.CONCLUIDA]: 'Concluir',
  [SITUACAO.ARQUIVADA]: 'Arquivar'
}

export const rotuloTransicao = (situacaoId?: number | null, fallback?: string | null): string =>
  ROTULO_TRANSICAO[situacaoId as SituacaoProjecao] ?? fallback ?? 'Mover'
